'use client';
import { useMemo, useState } from 'react';
import { Drawer, TextInput, ActionIcon, Text, Group, Box } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { tokenizePartQuery, buildPartHaystack, partMatchesTokens } from '@/lib/partSearch';
import { sortColorOptions } from '@/lib/legoColors';
import { getThumbUrl } from '@/lib/imageUrl';

// Zoek-overlay binnen Sort mode: "waar hoort dit steentje?" beantwoorden
// zonder de modus te verlaten. Zelfde matcher als de overzichtspagina
// (dimensie-normalisatie, kleurnamen in de zoektekst) + kleurchips.
// Een tik op een zak-chip springt naar die zak en licht de tegel op.
export function SortSearchSheet({ opened, onClose, bags, chipLabel, onJumpToPart }) {
  const [query, setQuery] = useState('');
  const [selectedColors, setSelectedColors] = useState([]);
  const filterActive = query.trim().length > 0 || selectedColors.length > 0;

  const colorOptions = useMemo(() => {
    const byColor = new Map();
    bags.forEach(bag => {
      (bag.parts || []).forEach(p => {
        if (!p.colorName) return;
        const cur = byColor.get(p.colorName) || { name: p.colorName, hex: p.colorHex, pieces: 0 };
        cur.pieces += p.quantity || 0;
        if (!cur.hex && p.colorHex) cur.hex = p.colorHex;
        byColor.set(p.colorName, cur);
      });
    });
    return sortColorOptions([...byColor.values()]);
  }, [bags]);

  const results = useMemo(() => {
    if (!filterActive) return [];
    const tokens = tokenizePartQuery(query);
    const colorSet = new Set(selectedColors);
    const seen = new Map();
    bags.forEach((bag, bagIndex) => {
      (bag.parts || []).forEach(p => {
        if (colorSet.size > 0 && !colorSet.has(p.colorName)) return;
        if (tokens.length > 0 && !partMatchesTokens(buildPartHaystack(p), tokens)) return;
        const key = `${p.partId}_${p.color_id || 'nocolor'}`;
        if (!seen.has(key)) seen.set(key, { key, part: p, total: 0, locations: [] });
        const entry = seen.get(key);
        entry.total += p.quantity || 0;
        entry.locations.push({ bagIndex, bag, quantity: p.quantity || 0 });
      });
    });
    return [...seen.values()].sort((a, b) => b.total - a.total);
  }, [filterActive, query, selectedColors, bags]);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="bottom"
      title={<Text fw={700}>Find a part</Text>}
      radius="lg"
      size="auto"
      zIndex={400}
      styles={{
        inner: { alignItems: 'flex-end' },
        content: { height: 'auto', minHeight: 0, maxHeight: '85dvh', display: 'flex', flexDirection: 'column' },
        body: { overflowY: 'auto', minHeight: 0 },
      }}
    >
      <TextInput
        placeholder="Search parts by name, color or ID..."
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        rightSection={filterActive ? (
          <ActionIcon variant="subtle" color="gray" radius="xl" size="sm" aria-label="Clear search" onClick={() => { setQuery(''); setSelectedColors([]); }}>
            <IconX size={14} />
          </ActionIcon>
        ) : null}
        radius="xl"
        size="md"
        data-autofocus
      />

      {colorOptions.length > 1 && (
        <Group gap={6} mt={10}>
          {colorOptions.map((c) => {
            const selected = selectedColors.includes(c.name);
            return (
              <Box
                key={c.name}
                component="button"
                type="button"
                onClick={() => setSelectedColors(prev => selected ? prev.filter(n => n !== c.name) : [...prev, c.name])}
                aria-pressed={selected}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
                  fontSize: 12.5, fontWeight: selected ? 700 : 500, fontFamily: 'inherit',
                  backgroundColor: selected ? '#e7f5ff' : '#f8f9fa',
                  color: selected ? '#1971c2' : '#495057',
                  border: selected ? '1px solid #74c0fc' : '1px solid #e9ecef',
                  transition: 'all 0.15s ease',
                }}
              >
                {c.hex && (
                  <Box style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: `#${String(c.hex).replace(/^#/, '')}`, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                )}
                {c.name}
              </Box>
            );
          })}
        </Group>
      )}

      {filterActive && (
        <Box mt="sm">
          {results.length === 0 ? (
            <Text size="sm" c="dimmed" py="md">No parts match your search.</Text>
          ) : (
            <>
              <Text size="sm" fw={600} mb={4}>
                {results.length} part{results.length !== 1 ? 's' : ''} · {results.reduce((s, r) => s + r.total, 0)} pieces found
              </Text>
              {results.slice(0, 40).map((result) => (
                <Group key={result.key} gap="sm" wrap="nowrap" py={8} style={{ borderTop: '1px solid #f1f3f4' }}>
                  <Box style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <img
                      src={getThumbUrl(result.part.imageUrl, 96)}
                      alt={result.part.name || `Part ${result.part.partId}`}
                      style={{ maxWidth: 44, maxHeight: 44, mixBlendMode: 'multiply' }}
                      loading="lazy"
                    />
                  </Box>
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Text size="sm" fw={600} lineClamp={1}>{result.part.name || `Part ${result.part.partId}`}</Text>
                    <Group gap={6} wrap="nowrap">
                      {result.part.colorHex && (
                        <Box style={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: `#${String(result.part.colorHex).replace(/^#/, '')}`, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                      )}
                      <Text size="xs" c="dimmed" truncate>
                        {[result.part.colorName, result.part.partId].filter(Boolean).join(' · ')} · {result.total}x
                      </Text>
                    </Group>
                  </Box>
                  <Group gap={4} justify="flex-end" style={{ flexShrink: 0, maxWidth: '45%' }}>
                    {result.locations.map((loc) => (
                      <Box
                        key={loc.bag.bagNumber}
                        component="button"
                        type="button"
                        onClick={() => onJumpToPart(loc.bagIndex, result.key)}
                        style={{
                          border: 'none', cursor: 'pointer', fontFamily: 'inherit',
                          borderRadius: 999, padding: '4px 10px', fontSize: 12, fontWeight: 600,
                          backgroundColor: '#f1f3f5', color: '#495057', whiteSpace: 'nowrap',
                        }}
                      >
                        {chipLabel(loc.bag)} · {loc.quantity}x
                      </Box>
                    ))}
                  </Group>
                </Group>
              ))}
              {results.length > 40 && (
                <Text size="xs" c="dimmed" mt="xs">Showing the first 40 results. Refine your search to see the rest.</Text>
              )}
            </>
          )}
        </Box>
      )}
    </Drawer>
  );
}
